import React, { useState, useRef, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Send,
  Sparkles,
  Loader2,
  Globe,
  Bot,
  User,
  Workflow,
  Database,
  Layers,
  HelpCircle,
  Cpu,
  ArrowUpRight,
  Clipboard,
  Check,
  ChevronRight,
  Settings,
  MessageSquare
} from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";

const SPECIALTIES = [
  {
    id: "general",
    name: "General Consultant",
    role: "General automation consultant",
    badge: "General",
    icon: Sparkles,
    color: "#00F0FF",
    desc: "A versatile advisor for workflow architecture, system design, and overall business operations automation.",
    samples: [
      "Map a typical customer onboarding automation flow",
      "Recommend an AI tech stack for a growing SaaS startup",
      "How to replace busywork in a marketing team with AI",
      "Explain n8n vs Make.com for business automation"
    ]
  },
  {
    id: "crm",
    name: "CRM Automator",
    role: "CRM sync & lead router specialist",
    badge: "CRM & Leads",
    icon: Workflow,
    color: "#00FF66",
    desc: "Deep integration expert for HubSpot, Salesforce, active campaigns, lead routing, scoring, and automated cleanup.",
    samples: [
      "Automate inbound lead routing to Slack and Salesforce",
      "How to sync HubSpot contacts to Google Sheets bi-directionally",
      "Set up lead scoring using OpenAI and database lookups",
      "Best way to clean up duplicate leads in HubSpot automatically"
    ]
  },
  {
    id: "document",
    name: "DocuParser OCR",
    role: "Invoicing & Document AI agent",
    badge: "Documents",
    icon: Database,
    color: "#DFFF00",
    desc: "Extract text and structured JSON from PDF invoices, contracts, receipts, and bank statements automatically.",
    samples: [
      "Extract line items from invoice PDFs and save to QuickBooks",
      "Automate reading email attachments (receipts) and pushing to Google Drive",
      "Create a Python script to parse lease contracts with OCR",
      "Design an n8n flow to extract data from vendor receipts"
    ]
  },
  {
    id: "support",
    name: "Support Copilot",
    role: "24/7 support & ticket agent",
    badge: "Customer Support",
    icon: Bot,
    color: "#FF3366",
    desc: "Build self-improving support bots, hook up Zendesk/Intercom triggers, and create intelligent human escalation filters.",
    samples: [
      "Design a human-in-the-loop support handoff rule",
      "Build a Slack support bot connected to a Notion knowledge base",
      "How to automatically tag incoming support tickets by urgency",
      "Integrate Twilio voice bot with live Zendesk agent escalation"
    ]
  },
  {
    id: "growth",
    name: "Growth Engine",
    role: "Lead enrichment & outreach specialist",
    badge: "Growth",
    icon: Layers,
    color: "#BC13FE",
    desc: "Scale outreach systems, run automated database enrichment, create customized cold emails, and secure high deliverability.",
    samples: [
      "Scrape search results, enrich contacts via Apollo, and write cold email draft",
      "Set up an automated LinkedIn outbound outreach sequence",
      "Build a lead enrichment flow using Clay and ChatGPT",
      "Maximize email deliverability for a cold outbound domain stack"
    ]
  }
];

export const AgentPage = ({ onOpenLead }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [specialty, setSpecialty] = useState(SPECIALTIES[0]);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const [sessionId, setSessionId] = useState(null);
  const scrollRef = useRef(null);
  const stepsTimerRef = useRef(null);

  // Stepper state variables
  const thoughtSteps = [
    "Analyzing query intent & parameters...",
    "Querying DuckDuckGo live web search index...",
    "Filtering & ranking search results...",
    "Synthesizing answer using Gemini model..."
  ];

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, loading, stepIndex]);

  useEffect(() => {
    if (loading) {
      setStepIndex(0);
      let step = 0;
      stepsTimerRef.current = setInterval(() => {
        if (step < thoughtSteps.length - 1) {
          step += 1;
          setStepIndex(step);
        }
      }, 1500);
    } else {
      if (stepsTimerRef.current) {
        clearInterval(stepsTimerRef.current);
      }
    }
    return () => {
      if (stepsTimerRef.current) {
        clearInterval(stepsTimerRef.current);
      }
    };
  }, [loading]);

  const send = async (textArg) => {
    const text = (textArg ?? input).trim();
    if (!text || loading) return;

    const next = [...messages, { role: "user", content: text }];
    setMessages(next);
    setInput("");
    setLoading(true);

    try {
      const { data } = await api.post("/agent/chat", {
        prompt: text,
        session_id: sessionId,
        history: messages.slice(-6),
        specialty: specialty.id
      });
      setSessionId(data.session_id);
      setMessages([
        ...next,
        {
          role: "assistant",
          content: data.answer,
          sources: data.sources || [],
          model: data.model,
          specialtyId: specialty.id
        }
      ]);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Agent failed. Try again.";
      toast.error(typeof msg === "string" ? msg : "Agent failure");
    } finally {
      setLoading(false);
    }
  };

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  // Safe inline markdown element generator
  const parseInlineCode = (text) => {
    const codeParts = text.split(/(`.*?`)/g);
    return codeParts.map((cp, cIdx) => {
      if (cp.startsWith("`") && cp.endsWith("`")) {
        return (
          <code key={cIdx} className="px-1.5 py-0.5 rounded bg-white/10 font-mono text-xs text-cyan-400">
            {cp.slice(1, -1)}
          </code>
        );
      }
      return cp;
    });
  };

  const parseInline = (text) => {
    const boldParts = text.split(/(\*\*.*?\*\*)/g);
    return boldParts.map((bp, bIdx) => {
      if (bp.startsWith("**") && bp.endsWith("**")) {
        const inner = bp.slice(2, -2);
        return (
          <strong key={bIdx} className="font-bold text-white">
            {parseInlineCode(inner)}
          </strong>
        );
      }
      return parseInlineCode(bp);
    });
  };

  const renderMarkdown = (text) => {
    if (!text) return "";
    const parts = text.split(/(```[\s\S]*?```)/g);
    return parts.map((part, idx) => {
      if (part.startsWith("```")) {
        const match = part.match(/```(\w*)\n([\s\S]*?)```/);
        const lang = match ? match[1] : "";
        const code = match ? match[2] : part.slice(3, -3);
        return (
          <div key={idx} className="my-4 rounded-xl overflow-hidden border border-white/10 bg-black/60 font-mono text-xs">
            <div className="flex items-center justify-between px-4 py-2 border-b border-white/10 bg-white/5 text-zinc-400">
              <span>{lang || "code"}</span>
              <button
                onClick={() => {
                  navigator.clipboard.writeText(code.trim());
                  toast.success("Code copied to clipboard!");
                }}
                className="hover:text-white transition-colors flex items-center gap-1.5 py-1 px-2.5 rounded hover:bg-white/5"
              >
                <Clipboard className="w-3 h-3" />
                <span>Copy Code</span>
              </button>
            </div>
            <pre className="p-4 overflow-x-auto text-zinc-100 whitespace-pre">
              {code.trim()}
            </pre>
          </div>
        );
      } else {
        const lines = part.split("\n");
        return lines.map((line, lIdx) => {
          let content = line;
          if (content.startsWith("### ")) {
            return (
              <h4 key={`${idx}-${lIdx}`} className="text-base font-bold text-white mt-4 mb-2">
                {parseInline(content.slice(4))}
              </h4>
            );
          } else if (content.startsWith("## ")) {
            return (
              <h3 key={`${idx}-${lIdx}`} className="text-lg font-bold text-white mt-5 mb-2">
                {parseInline(content.slice(3))}
              </h3>
            );
          } else if (content.startsWith("# ")) {
            return (
              <h2 key={`${idx}-${lIdx}`} className="text-xl font-black text-white mt-6 mb-3">
                {parseInline(content.slice(2))}
              </h2>
            );
          }
          if (content.startsWith("- ") || content.startsWith("* ")) {
            return (
              <ul key={`${idx}-${lIdx}`} className="list-disc pl-5 my-1 space-y-1 text-zinc-300">
                <li>{parseInline(content.slice(2))}</li>
              </ul>
            );
          }
          const numMatch = content.match(/^(\d+)\.\s(.*)/);
          if (numMatch) {
            return (
              <ol key={`${idx}-${lIdx}`} className="list-decimal pl-5 my-1 space-y-1 text-zinc-300" start={parseInt(numMatch[1])}>
                <li>{parseInline(numMatch[2])}</li>
              </ol>
            );
          }
          if (content.trim() === "") {
            return <div key={`${idx}-${lIdx}`} className="h-2" />;
          }
          return (
            <p key={`${idx}-${lIdx}`} className="my-1.5 text-zinc-300 leading-relaxed">
              {parseInline(content)}
            </p>
          );
        });
      }
    });
  };

  const handleActionClick = (actionType, content, spec) => {
    if (actionType === "copy") {
      navigator.clipboard.writeText(content);
      toast.success("Solution copied to clipboard!");
    } else if (actionType === "configure") {
      // Open lead modal and pre-fill automation needs
      const element = document.querySelector('[data-testid="lead-modal-needs"]');
      if (element) {
        element.value = `Specialty: ${spec.name}\nProposed Workflow Summary:\n${content.slice(0, 500)}...`;
      }
      localStorage.setItem("pending_needs", `Specialty: ${spec.name}\nProposed Workflow Summary:\n${content}`);
      onOpenLead?.();
    } else if (actionType === "consult") {
      navigate("/consult", {
        state: {
          needs: `Discussing solution for ${spec.name} agent response:\n\n${content.slice(0, 300)}...`
        }
      });
    }
  };

  const activeIcon = () => {
    const Icon = specialty.icon;
    return <Icon className="w-5 h-5" style={{ color: specialty.color }} />;
  };

  return (
    <div className="min-h-screen pt-24 pb-12 relative flex flex-col md:flex-row max-w-7xl mx-auto px-6 md:px-10 gap-6" data-testid="agent-page">
      {/* Sidebar - Specialties Selector */}
      <div className="w-full md:w-80 shrink-0 flex flex-col gap-4">
        <div className="glass rounded-3xl p-6">
          <div className="flex items-center gap-2 mb-4">
            <Settings className="w-4 h-4 text-[#00F0FF]" />
            <h2 className="text-xs uppercase tracking-[0.25em] text-zinc-400 font-bold">
              Agent Specialties
            </h2>
          </div>
          <div className="space-y-2">
            {SPECIALTIES.map((spec) => {
              const SpecIcon = spec.icon;
              const isActive = specialty.id === spec.id;
              return (
                <button
                  key={spec.id}
                  onClick={() => {
                    setSpecialty(spec);
                    setSessionId(null);
                    setMessages([]);
                  }}
                  className={`w-full text-left p-3.5 rounded-2xl border transition-all flex items-start gap-3.5 ${
                    isActive
                      ? "bg-white/[0.06] border-white/20 shadow-[0_0_20px_rgba(0,240,255,0.06)]"
                      : "bg-transparent border-transparent hover:bg-white/[0.03] hover:border-white/5"
                  }`}
                >
                  <span
                    className="p-2.5 rounded-xl border flex items-center justify-center shrink-0"
                    style={{
                      backgroundColor: isActive ? `${spec.color}15` : "rgba(255,255,255,0.02)",
                      borderColor: isActive ? `${spec.color}40` : "rgba(255,255,255,0.08)"
                    }}
                  >
                    <SpecIcon className="w-4 h-4" style={{ color: spec.color }} />
                  </span>
                  <div>
                    <div className="flex items-center gap-1.5">
                      <span className="text-sm font-bold text-white">{spec.name}</span>
                      {isActive && (
                        <span className="w-1.5 h-1.5 rounded-full pulse-glow" style={{ backgroundColor: spec.color }} />
                      )}
                    </div>
                    <span className="text-[11px] text-zinc-500 block leading-tight mt-0.5">
                      {spec.role}
                    </span>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Specialty Description */}
        <div className="glass rounded-3xl p-6 hidden md:block">
          <h3 className="text-sm font-bold text-white mb-2 flex items-center gap-2">
            {activeIcon()}
            <span>About this Agent</span>
          </h3>
          <p className="text-xs text-zinc-400 leading-relaxed">
            {specialty.desc}
          </p>
        </div>
      </div>

      {/* Main Workspace - Chat Area */}
      <div className="flex-1 flex flex-col glass rounded-3xl overflow-hidden min-h-[580px] shadow-[0_0_80px_rgba(0,240,255,0.1)]">
        {/* Workspace Header */}
        <div className="px-6 py-5 border-b border-white/10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 bg-black/20">
          <div className="flex items-center gap-3">
            <span
              className="w-10 h-10 rounded-xl border flex items-center justify-center relative shadow-lg"
              style={{
                backgroundColor: `${specialty.color}12`,
                borderColor: `${specialty.color}35`
              }}
            >
              {activeIcon()}
              <span className="absolute bottom-0 right-0 w-2.5 h-2.5 rounded-full bg-emerald-500 border border-black" />
            </span>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-base font-bold text-white leading-none">
                  {specialty.name}
                </h1>
                <span
                  className="text-[9px] uppercase tracking-wider px-2 py-0.5 rounded-full font-bold border"
                  style={{
                    color: specialty.color,
                    borderColor: `${specialty.color}40`,
                    backgroundColor: `${specialty.color}10`
                  }}
                >
                  {specialty.badge}
                </span>
              </div>
              <p className="text-xs text-zinc-500 font-mono mt-1">
                gemini-1.5-flash · active session
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1.5 text-xs text-zinc-400 bg-white/5 border border-white/10 rounded-full px-3 py-1">
              <Globe className="w-3.5 h-3.5 text-cyan-400" />
              <span>DDG Grounding On</span>
            </div>
          </div>
        </div>

        {/* Workspace Body / Chat Feed */}
        <div
          ref={scrollRef}
          className="flex-1 px-6 py-6 space-y-6 overflow-y-auto max-h-[480px] bg-gradient-to-b from-black/5 to-black/35"
        >
          {messages.length === 0 && (
            <div className="max-w-2xl mx-auto text-center py-12">
              <div className="w-12 h-12 rounded-2xl bg-white/[0.03] border border-white/10 flex items-center justify-center mx-auto mb-4 animate-float">
                <Bot className="w-6 h-6 text-cyan-400" />
              </div>
              <h2 className="text-lg font-bold text-white tracking-tight">
                Let's automate your back-office
              </h2>
              <p className="text-sm text-zinc-400 mt-2 max-w-md mx-auto leading-relaxed">
                Describe a system or process you want automated. This specialized agent will plan, structure, and provide a workable solution citing live web sources.
              </p>

              {/* Sample Prompts */}
              <div className="mt-8">
                <p className="text-xs uppercase tracking-wider text-zinc-500 font-bold mb-3">
                  Click a starter prompt below:
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 max-w-xl mx-auto">
                  {specialty.samples.map((s, idx) => (
                    <button
                      key={idx}
                      onClick={() => send(s)}
                      className="text-left text-xs p-3 rounded-xl border border-white/5 bg-white/[0.02] hover:bg-white/[0.05] hover:border-cyan-400/40 text-zinc-300 transition-all flex items-center justify-between group"
                    >
                      <span className="line-clamp-2">{s}</span>
                      <ChevronRight className="w-3.5 h-3.5 shrink-0 text-zinc-600 group-hover:text-cyan-400 transition-colors ml-2" />
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {messages.map((m, idx) => {
            const isUser = m.role === "user";
            const currentSpec = SPECIALTIES.find((s) => s.id === m.specialtyId) || specialty;
            return (
              <div key={idx} className={`flex gap-4 ${isUser ? "flex-row-reverse" : "flex-row animate-fade-up"}`}>
                {/* Avatar */}
                <div
                  className={`w-9 h-9 rounded-xl border flex items-center justify-center shrink-0 shadow-md ${
                    isUser
                      ? "border-white/15 bg-white/5"
                      : "border-cyan-400/30"
                  }`}
                  style={{
                    backgroundColor: isUser ? "" : `${currentSpec.color}15`,
                    borderColor: isUser ? "" : `${currentSpec.color}35`
                  }}
                >
                  {isUser ? (
                    <User className="w-4 h-4 text-zinc-300" />
                  ) : (
                    (() => {
                      const SpecIcon = currentSpec.icon;
                      return <SpecIcon className="w-4 h-4" style={{ color: currentSpec.color }} />;
                    })()
                  )}
                </div>

                {/* Content Bubble */}
                <div className="max-w-[85%] flex flex-col gap-1.5">
                  <div className="text-[10px] text-zinc-500 font-mono tracking-wider">
                    {isUser ? "USER" : currentSpec.name.toUpperCase()}
                  </div>
                  <div
                    className={`rounded-2xl px-5 py-4 text-sm leading-relaxed border ${
                      isUser
                        ? "bg-white text-black border-white"
                        : "bg-white/[0.03] border-white/10 text-zinc-100"
                    }`}
                  >
                    {isUser ? m.content : renderMarkdown(m.content)}

                    {/* Sources Block */}
                    {!isUser && m.sources?.length > 0 && (
                      <div className="mt-4 pt-4 border-t border-white/10">
                        <p className="text-[10px] uppercase tracking-[0.2em] text-cyan-400 font-bold mb-2">
                          Grounding References
                        </p>
                        <ul className="space-y-1.5">
                          {m.sources.slice(0, 4).map((s, sIdx) => (
                            <li key={sIdx} className="text-xs text-zinc-400 flex items-start gap-1.5">
                              <span className="text-cyan-400 font-mono text-[10px] mt-0.5">[{sIdx + 1}]</span>
                              <a
                                href={s.url}
                                target="_blank"
                                rel="noreferrer"
                                className="hover:text-cyan-400 transition-colors underline-offset-2 hover:underline line-clamp-1 flex items-center gap-1"
                              >
                                {s.title || s.url}
                                <ArrowUpRight className="w-3 h-3 text-zinc-600" />
                              </a>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>

                  {/* Actions Footer */}
                  {!isUser && (
                    <div className="flex flex-wrap items-center gap-2 mt-2">
                      <button
                        onClick={() => handleActionClick("configure", m.content, currentSpec)}
                        className="text-[11px] font-bold text-white hover:text-black hover:bg-cyan-400 border border-white/15 bg-white/5 rounded-lg px-3 py-1.5 flex items-center gap-1.5 transition-all"
                      >
                        <Cpu className="w-3 h-3" />
                        <span>Deploy This Automation</span>
                      </button>
                      <button
                        onClick={() => handleActionClick("consult", m.content, currentSpec)}
                        className="text-[11px] font-bold text-zinc-400 hover:text-white border border-white/10 bg-transparent rounded-lg px-3 py-1.5 flex items-center gap-1.5 hover:bg-white/5 transition-all"
                      >
                        <MessageSquare className="w-3 h-3" />
                        <span>Talk to Engineer</span>
                      </button>
                      <button
                        onClick={() => handleActionClick("copy", m.content, currentSpec)}
                        className="text-[11px] text-zinc-500 hover:text-zinc-300 py-1.5 px-2 flex items-center gap-1 hover:bg-white/5 rounded-lg transition-all"
                      >
                        <Clipboard className="w-3 h-3" />
                        <span>Copy Solution</span>
                      </button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}

          {/* Thought Stepper Loader */}
          {loading && (
            <div className="flex gap-4 animate-pulse">
              <div
                className="w-9 h-9 rounded-xl border flex items-center justify-center shrink-0"
                style={{
                  backgroundColor: `${specialty.color}15`,
                  borderColor: `${specialty.color}35`
                }}
              >
                <Loader2 className="w-4 h-4 animate-spin" style={{ color: specialty.color }} />
              </div>
              <div className="max-w-[85%] space-y-2 mt-1">
                <span className="text-[10px] text-zinc-500 font-mono tracking-wider">
                  THINKING PROCESS
                </span>
                <div className="bg-white/[0.02] border border-white/5 rounded-2xl px-5 py-4 min-w-[280px]">
                  {/* Thought stepper list */}
                  <div className="space-y-3">
                    {thoughtSteps.map((step, idx) => {
                      const isActive = stepIndex === idx;
                      const isCompleted = stepIndex > idx;
                      return (
                        <div key={idx} className="flex items-center gap-2.5 text-xs">
                          {isCompleted ? (
                            <span className="w-4 h-4 rounded-full bg-cyan-400/20 border border-cyan-400/40 flex items-center justify-center">
                              <Check className="w-2.5 h-2.5 text-cyan-400" />
                            </span>
                          ) : isActive ? (
                            <Loader2 className="w-4 h-4 animate-spin text-cyan-400 shrink-0" />
                          ) : (
                            <span className="w-4 h-4 rounded-full border border-white/10 bg-white/2 shrink-0" />
                          )}
                          <span
                            className={
                              isCompleted
                                ? "text-zinc-500 line-through"
                                : isActive
                                ? "text-white font-bold"
                                : "text-zinc-600"
                            }
                          >
                            {step}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Input Composer */}
        <div className="px-6 py-5 border-t border-white/10 bg-black/40">
          <div className="flex items-end gap-3 max-w-4xl mx-auto">
            <textarea
              data-testid="agent-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={onKey}
              rows={1}
              placeholder={`Ask ${specialty.name} e.g. '${specialty.samples[0]}'`}
              className="flex-1 bg-white/[0.03] border border-white/10 rounded-2xl px-5 py-3.5 text-sm text-white placeholder:text-zinc-600 focus:border-cyan-400 focus:ring-1 focus:ring-cyan-400 outline-none resize-none max-h-36 transition-all"
            />
            <button
              data-testid="agent-send-btn"
              onClick={() => send()}
              disabled={loading || !input.trim()}
              className="h-12 w-12 sm:w-auto sm:px-6 rounded-2xl text-black font-bold text-sm flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed transition-all hover:scale-105 active:scale-95 shadow-lg"
              style={{
                backgroundColor: specialty.color
              }}
            >
              <Send className="w-4 h-4" />
              <span className="hidden sm:inline">Submit Query</span>
            </button>
          </div>
          <div className="flex items-center justify-between mt-3 text-[10px] text-zinc-600 font-mono tracking-wider max-w-4xl mx-auto">
            <span>POWERED BY GOOGLE GEMINI</span>
            <span>CITES LIVE GOOGLE & DUCKDUKGO WEB RESULTS</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AgentPage;
